import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useLoadScript } from "@react-google-maps/api";
import {
  getDeliverySettings,
  updateDeliverySettings,
  createDeliveryDriver,
  getDeliveryDrivers,
  updateDeliveryDriver,
  verifyDeliveryDriver,
  recalculateDriverOrderCount,
  deleteDeliveryDriver,
  getDeliveryLogs,
  getDeliveryLogsSummary,
  getBroadcastLogs,
} from "../../services/ClientService";

const GOOGLE_MAPS_LIBRARIES = ["places"];

// --- Delivery Metrics Helpers ---
const formatDuration = (ms) => {
  if (!ms || ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h} ${h === 1 ? 'Hour' : 'Hours'} ${m} Min ${s} Sec`;
  if (m > 0) return `${m} Min ${s} Sec`;
  return `${s} Sec`;
};

const formatDateForInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const DeliveryControls = () => {
  const userData = JSON.parse(localStorage.getItem("userData"));
  const [activeTab, setActiveTab] = useState("logs");
  const [loading, setLoading] = useState(false);

  // Google Maps
  const { isLoaded: isMapsLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyBE4x7mA5JreDo4WjA9TvWpESAzLB4Kf_U",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const editAddressInputRef = useRef(null);
  const editAutocompleteRef = useRef(null);

  const emptyDriverForm = {
    name: "",
    email: "",
    phone: "",
    drivingLicenseNumber: "",
    drivingLicenseExpiry: "",
    vehicleType: "bike",
    vehicleNumber: "",
    address: "",
    addressCoordinates: null,
    emergencyContact: "",
  };

  // Settings state
  const [settings, setSettings] = useState({
    autoAssignDriver: true,
    assignmentRadius: 10,
    maxOrdersPerDriver: 5,
    otpVerificationRequired: true,
    allowDriverCancellation: false,
    notifyCustomerOnAssignment: true,
    autoBroadcastToDrivers: false,
    mapVisibility: {
      mode: 'show_from_status',
      showFromStatus: 'Confirmed',
    },
    deliveryOpeningTime: '08:00',
    deliveryClosingTime: '22:00',
  });

  // Drivers state
  const [drivers, setDrivers] = useState([]);
  const [driverSearch, setDriverSearch] = useState("");
  const [driverStatusFilter, setDriverStatusFilter] = useState("all");
  const [showCreateDriverModal, setShowCreateDriverModal] = useState(false);
  const [showEditDriverModal, setShowEditDriverModal] = useState(false);
  const [driverForm, setDriverForm] = useState({ ...emptyDriverForm });
  const [editDriverForm, setEditDriverForm] = useState({ _id: "", ...emptyDriverForm });

  // Delivery Logs state (pagination like inventory-logs)
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [logsLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  // Logs filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Summary state (replaces Dashboard)
  const [summary, setSummary] = useState(null);
  const [driverList, setDriverList] = useState([]);
  const [expandedLog, setExpandedLog] = useState(null);

  // Broadcast logs state
  const [broadcastLogsCache, setBroadcastLogsCache] = useState({});
  const [loadingBroadcastLog, setLoadingBroadcastLog] = useState(null);



  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      const res = await getDeliverySettings();
      if (res.success && res.data) {
        setSettings((prev) => ({
          ...prev,
          ...res.data,
          mapVisibility: res.data.mapVisibility || { mode: 'show_from_status', showFromStatus: 'Confirmed' },
        }));
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  }, []);

  // Load drivers
  const loadDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getDeliveryDrivers({
        status: driverStatusFilter,
        search: driverSearch,
      });
      if (res.success) {
        setDrivers(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load drivers:", err);
    } finally {
      setLoading(false);
    }
  }, [driverSearch, driverStatusFilter]);

  // Load delivery logs with pagination
  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const skip = (currentPage - 1) * logsLimit;
      const payload = { skip, limit: logsLimit };
      if (debouncedSearch) payload.searchTerm = debouncedSearch;
      if (selectedDriver) payload.driverId = selectedDriver;
      if (selectedStatus && selectedStatus !== "all") payload.status = selectedStatus;
      if (selectedPaymentMethod && selectedPaymentMethod !== "all") payload.paymentMethod = selectedPaymentMethod;
      if (startDate) payload.startDate = startDate;
      if (endDate) payload.endDate = endDate;

      const res = await getDeliveryLogs(payload);
      if (res.success) {
        setLogs(res.data || []);
        const pg = res.pagination || {};
        setTotalCount(pg.totalCount || 0);
        setTotalPages(pg.totalPages || 0);
        setHasNextPage(pg.hasMore || false);
        setHasPrevPage(currentPage > 1);
      }
    } catch (err) {
      console.error("Failed to load delivery logs:", err);
    } finally {
      setLogsLoading(false);
    }
  }, [currentPage, logsLimit, debouncedSearch, selectedDriver, selectedStatus, selectedPaymentMethod, startDate, endDate]);

  // Load summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await getDeliveryLogsSummary();
      if (res.success) {
        setSummary(res.data);
        setDriverList(res.data?.driverList || []);
      }
    } catch (err) {
      console.error("Failed to load summary:", err);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (activeTab === "drivers") loadDrivers();
  }, [activeTab, loadDrivers]);

  useEffect(() => {
    if (activeTab === "logs") {
      fetchLogs();
      fetchSummary();
    }
  }, [activeTab, fetchLogs, fetchSummary]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedDriver, selectedStatus, selectedPaymentMethod, startDate, endDate]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedDriver("");
    setSelectedStatus("all");
    setSelectedPaymentMethod("all");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  // Save settings
  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      const res = await updateDeliverySettings(settings);
      if (res.success) {
        toast.success("Delivery settings saved!");
      } else {
        toast.error(res.message || "Failed to save settings");
      }
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  // Fetch broadcast logs for a specific order
  const fetchBroadcastLog = async (orderId) => {
    if (broadcastLogsCache[orderId]) return;
    setLoadingBroadcastLog(orderId);
    try {
      const res = await getBroadcastLogs({ orderId });
      if (res.success && res.data) {
        setBroadcastLogsCache((prev) => ({ ...prev, [orderId]: res.data }));
      }
    } catch (err) {
      console.error("Failed to load broadcast logs:", err);
    } finally {
      setLoadingBroadcastLog(null);
    }
  };

  // Initialize Google Maps Autocomplete when modal opens
  useEffect(() => {
    if (showCreateDriverModal && isMapsLoaded && addressInputRef.current && !autocompleteRef.current) {
      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            types: ["geocode", "establishment"],
            fields: ["formatted_address", "geometry", "name", "address_components"],
          }
        );
        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current.getPlace();
          if (!place.geometry) return;
          let detailedAddress = place.formatted_address;
          if (place.name && !detailedAddress.includes(place.name)) {
            detailedAddress = `${place.name}, ${detailedAddress}`;
          }
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setDriverForm((prev) => ({
            ...prev,
            address: detailedAddress,
            addressCoordinates: { lat, lng },
          }));
        });
      } catch (error) {
        console.error("Google Maps Autocomplete initialization failed:", error);
      }
    }
    // Cleanup on modal close
    if (!showCreateDriverModal) {
      autocompleteRef.current = null;
    }
  }, [showCreateDriverModal, isMapsLoaded]);

  // Initialize Google Maps Autocomplete for edit driver modal
  useEffect(() => {
    if (showEditDriverModal && isMapsLoaded && editAddressInputRef.current && !editAutocompleteRef.current) {
      try {
        editAutocompleteRef.current = new window.google.maps.places.Autocomplete(
          editAddressInputRef.current,
          {
            types: ["geocode", "establishment"],
            fields: ["formatted_address", "geometry", "name", "address_components"],
          }
        );
        editAutocompleteRef.current.addListener("place_changed", () => {
          const place = editAutocompleteRef.current.getPlace();
          if (!place.geometry) return;
          let detailedAddress = place.formatted_address;
          if (place.name && !detailedAddress.includes(place.name)) {
            detailedAddress = `${place.name}, ${detailedAddress}`;
          }
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setEditDriverForm((prev) => ({
            ...prev,
            address: detailedAddress,
            addressCoordinates: { lat, lng },
          }));
        });
      } catch (error) {
        console.error("Google Maps Autocomplete initialization failed:", error);
      }
    }
    if (!showEditDriverModal) {
      editAutocompleteRef.current = null;
    }
  }, [showEditDriverModal, isMapsLoaded]);

  // Create driver
  const handleCreateDriver = async () => {
    if (!driverForm.name || !driverForm.email || !driverForm.phone || !driverForm.drivingLicenseNumber) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      setLoading(true);
      const res = await createDeliveryDriver(driverForm);
      if (res.success) {
        toast.success(`Driver created! Temp password: ${res.data?.tempPassword}`);
        setShowCreateDriverModal(false);
        setDriverForm({ ...emptyDriverForm });
        loadDrivers();
      } else {
        toast.error(res.message || "Failed to create driver");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to create driver");
    } finally {
      setLoading(false);
    }
  };

  const openEditDriver = (driver) => {
    setEditDriverForm({
      _id: driver._id,
      name: driver.name || "",
      email: driver.email || "",
      phone: driver.phone || "",
      drivingLicenseNumber: driver.drivingLicenseNumber || "",
      drivingLicenseExpiry: formatDateForInput(driver.drivingLicenseExpiry),
      vehicleType: driver.vehicleType || "bike",
      vehicleNumber: driver.vehicleNumber || "",
      address: driver.address || "",
      addressCoordinates: driver.addressCoordinates || null,
      emergencyContact: driver.emergencyContact || "",
    });
    setShowEditDriverModal(true);
  };

  const closeEditDriver = () => {
    setShowEditDriverModal(false);
    setEditDriverForm({ _id: "", ...emptyDriverForm });
    editAutocompleteRef.current = null;
  };

  const handleUpdateDriver = async () => {
    if (!editDriverForm.name || !editDriverForm.phone || !editDriverForm.drivingLicenseNumber) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        _id: editDriverForm._id,
        name: editDriverForm.name.trim(),
        phone: editDriverForm.phone.trim(),
        drivingLicenseNumber: editDriverForm.drivingLicenseNumber.trim(),
        vehicleType: editDriverForm.vehicleType,
        vehicleNumber: editDriverForm.vehicleNumber.trim(),
        address: editDriverForm.address.trim(),
        addressCoordinates: editDriverForm.addressCoordinates,
        emergencyContact: editDriverForm.emergencyContact.trim(),
      };
      if (editDriverForm.drivingLicenseExpiry) {
        payload.drivingLicenseExpiry = editDriverForm.drivingLicenseExpiry;
      }
      const res = await updateDeliveryDriver(payload);
      if (res.success) {
        toast.success("Driver details updated successfully");
        closeEditDriver();
        loadDrivers();
      } else {
        toast.error(res.message || "Failed to update driver");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update driver");
    } finally {
      setLoading(false);
    }
  };

  // Verify/unverify driver
  const handleVerifyDriver = async (driverId, isVerified) => {
    try {
      const res = await verifyDeliveryDriver({ _id: driverId, isVerified });
      if (res.success) {
        toast.success(isVerified ? "Driver verified!" : "Verification revoked");
        loadDrivers();
      }
    } catch (err) {
      toast.error("Failed to update verification");
    }
  };

  // Sync activeOrderCount from actual deliveryAssignments
  const handleSyncOrderCount = async (driverId) => {
    try {
      const res = await recalculateDriverOrderCount({ driverId });
      if (res.success) {
        toast.success(`Count synced: ${res.data?.previousCount} → ${res.data?.newCount}`);
        loadDrivers();
      }
    } catch (err) {
      toast.error("Failed to sync order count");
    }
  };

  const handleDeleteDriver = async (driver) => {
    const activeCount = Number(driver.activeOrderCount) || 0;
    if (activeCount > 0) {
      toast.error(`${driver.name} has ${activeCount} active order(s). Complete or reassign them before deleting.`);
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete delivery partner "${driver.name}"?\n\nThis cannot be undone. Their login will stop working immediately.`
    );
    if (!confirmed) return;

    try {
      const res = await deleteDeliveryDriver({ _id: driver._id });
      if (res.success) {
        toast.success(res.message || "Driver deleted successfully");
        loadDrivers();
      } else {
        toast.error(res.message || "Failed to delete driver");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete driver");
    }
  };

  // Suspend/activate driver
  const handleToggleDriverStatus = async (driverId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "suspended" : "active";
    try {
      const res = await updateDeliveryDriver({ _id: driverId, status: newStatus });
      if (res.success) {
        toast.success(`Driver ${newStatus === "active" ? "activated" : "suspended"}`);
        loadDrivers();
      }
    } catch (err) {
      toast.error("Failed to update driver status");
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const tabs = [
    { id: "logs", label: "Delivery Logs", icon: "📋" },
    { id: "drivers", label: "Delivery Partners", icon: "🚗" },
    { id: "settings", label: "Delivery Settings", icon: "⚙️" },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Delivery Controls</h1>
          <p className="text-gray-500 mt-1">Manage delivery partners, settings, and track deliveries</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-white rounded-lg p-1 shadow-sm mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Delivery Logs Tab */}
        {activeTab === "logs" && (
          <div>
            {/* Sticky Header: Search + Filter Toggle */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4 sticky top-0 z-10">
              <div className="flex flex-wrap gap-3 items-center justify-between">
                <div className="flex-1 min-w-[200px]">
                  <input
                    type="text"
                    placeholder="Search by order ID, driver, customer, branch..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      showFilters ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    🔽 Filters {(selectedDriver || selectedStatus !== "all" || selectedPaymentMethod !== "all" || startDate || endDate) && (
                      <span className="ml-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">!</span>
                    )}
                  </button>
                  {(searchTerm || selectedDriver || selectedStatus !== "all" || selectedPaymentMethod !== "all" || startDate || endDate) && (
                    <button onClick={clearFilters} className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg border border-red-200">
                      ✕ Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Expandable Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Driver</label>
                    <select
                      value={selectedDriver}
                      onChange={(e) => setSelectedDriver(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="">All Drivers</option>
                      {driverList.map((d) => (
                        <option key={d._id} value={d._id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="assigned">Assigned</option>
                      <option value="picked_up">Picked Up</option>
                      <option value="on_the_way">Out for Delivery</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Payment Type</label>
                    <select
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="all">All Types</option>
                      <option value="cod">COD (Cash on Delivery)</option>
                      <option value="online">Online Payment</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Cards (Dashboard stats inline) */}
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <SummaryCard title="Total Drivers" value={summary.drivers?.total || 0} subtitle={`${summary.drivers?.online || 0} online`} gradient="from-blue-500 to-blue-600" />
                <SummaryCard title="Active Drivers" value={summary.drivers?.active || 0} subtitle={`${summary.drivers?.pendingVerification || 0} pending`} gradient="from-green-500 to-green-600" />
                <SummaryCard title="Active Deliveries" value={summary.activeDeliveries || 0} subtitle="In progress" gradient="from-orange-500 to-orange-600" />
                <SummaryCard title="Delivered Today" value={summary.today?.deliveredToday || 0} subtitle={`${summary.today?.totalToday || 0} total today`} gradient="from-purple-500 to-purple-600" />
                <SummaryCard title="Cancelled" value={summary.byStatus?.cancelled || 0} subtitle={`${summary.today?.cancelledToday || 0} today`} gradient="from-red-500 to-red-600" />
                <SummaryCard title="Recent Activity" value={summary.recentActivityCount || 0} subtitle="Last 24 hours" gradient="from-indigo-500 to-indigo-600" />
              </div>
            )}

            {/* Logs Count */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-500 font-medium">
                {logsLoading ? 'Loading...' : (
                  <>Showing <span className="text-gray-800 font-semibold">{logs.length}</span> of <span className="text-gray-800 font-semibold">{totalCount}</span> delivery assignments
                  {currentPage > 1 && <span className="text-gray-400"> — Page {currentPage} of {totalPages}</span>}</>
                )}
              </p>
            </div>

            {/* Order Cards */}
            {logsLoading ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3" />
                <p className="text-sm font-medium">Loading delivery logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-sm border border-dashed border-gray-200">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3 text-2xl">🚚</div>
                <p className="text-gray-700 font-semibold text-base">No delivery logs found</p>
                <p className="text-gray-400 text-sm mt-1">Adjust your filters or wait for delivery activity</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {logs.map((log) => {
                  const isExpanded = expandedLog === log._id;
                  const statusAccent = {
                    delivered: 'border-l-green-500',
                    cancelled: 'border-l-red-400',
                    on_the_way: 'border-l-orange-400',
                    picked_up: 'border-l-indigo-400',
                    assigned: 'border-l-blue-400',
                  }[log.status] || 'border-l-gray-300';

                  const statusIcon = {
                    delivered: '✅',
                    cancelled: '❌',
                    on_the_way: '🛵',
                    picked_up: '📦',
                    assigned: '📋',
                  }[log.status] || '🚚';

                  const timelineIcons = {
                    assigned: '📋',
                    picked_up: '📦',
                    on_the_way: '🛵',
                    out_for_delivery: '🛵',
                    delivered: '✅',
                    cancelled: '❌',
                  };

                  return (
                    <div
                      key={log._id}
                      className={`bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${statusAccent} overflow-hidden transition-shadow hover:shadow-md`}
                    >
                      {/* Card Row */}
                      <div
                        className="px-5 py-4 cursor-pointer select-none"
                        onClick={() => {
                          const newExpanded = isExpanded ? null : log._id;
                          setExpandedLog(newExpanded);
                          if (newExpanded && log.orderId) {
                            fetchBroadcastLog(typeof log.orderId === 'object' ? log.orderId.toString() : log.orderId);
                          }

                        }}
                      >
                        <div className="flex items-center gap-4">

                          {/* Order Number Block */}
                          <div className="flex-shrink-0 w-[180px]">
                            <div className="flex items-center gap-2">
                              <span className="text-base">{statusIcon}</span>
                              <div className="min-w-0">
                                <p className="text-xs font-bold text-gray-900 truncate leading-tight">
                                  #{log.saleId || log.orderId?.toString().slice(-10)}
                                </p>
                                <p className="text-[10px] text-gray-400 leading-tight mt-0.5">
                                  {log.assignedAt
                                    ? new Date(log.assignedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
                                    : 'N/A'}
                                </p>
                                {log.branchName && (
                                  <p className="text-[10px] text-blue-500 truncate leading-tight">{log.branchName}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="hidden md:block w-px h-10 bg-gray-100 flex-shrink-0" />

                          {/* Driver */}
                          <div className="flex items-center gap-2.5 flex-shrink-0 w-[180px] hidden sm:flex">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
                              {log.driverName?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-gray-900 truncate leading-tight">{log.driverName || 'Unassigned'}</p>
                              <p className="text-[10px] text-gray-400 leading-tight">{log.driverPhone || '—'}</p>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="hidden lg:block w-px h-10 bg-gray-100 flex-shrink-0" />

                          {/* Customer */}
                          <div className="flex items-center gap-2.5 flex-1 min-w-0 hidden lg:flex">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
                              {log.customerName?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-gray-900 truncate leading-tight">{log.customerName || 'N/A'} - (Customer)</p>
                              <p className="text-[10px] text-gray-400 leading-tight">{log.customerPhone || '—'}</p>
                            </div>
                          </div>

                          {/* Amount + Payment */}
                          <div className="flex-shrink-0 text-right w-[90px] hidden sm:block">
                            {log.orderAmount != null && (
                              <p className="text-sm font-bold text-gray-900">₹{Number(log.orderAmount).toFixed(2)}</p>
                            )}
                            {log.paymentMethod && (
                              <span className="inline-block text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded capitalize">
                                {log.paymentMethod}
                              </span>
                            )}
                          </div>

                          {/* Time + Status + Chevron */}
                          <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                            <div className="hidden md:block text-right">
                              <p className="text-[10px] text-gray-400 leading-tight">
                                {log.assignedAt
                                  ? new Date(log.assignedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                                  : ''}
                              </p>
                            </div>
                            <LogStatusBadge status={log.status} />
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center bg-gray-100 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Detail Panel */}
                      {isExpanded && (
                        <div className="border-t border-gray-100">
                          <div className="p-5 bg-gradient-to-b from-gray-50 to-white">

                            {/* Delivery Metrics: Prep Time, Delivery Time */}
                            {(() => {
                              // Prep Time: Assigned → Picked Up
                              const pickedUpEntry = (log.statusHistory || []).find(s => s.status === 'picked_up');
                              const assignedEntry = (log.statusHistory || []).find(s => s.status === 'assigned');
                              const resolvedPickedUpAt = pickedUpEntry?.timestamp || log.pickedUpAt;
                              const resolvedAssignedAt = assignedEntry?.timestamp || log.assignedAt;

                              let prepTimeMs = null;
                              if (resolvedAssignedAt && resolvedPickedUpAt) {
                                prepTimeMs = new Date(resolvedPickedUpAt) - new Date(resolvedAssignedAt);
                              }

                              // Delivery Time: Picked Up → Delivered
                              const deliveredEntry = (log.statusHistory || []).find(s => s.status === 'delivered');
                              const resolvedDeliveredAt = deliveredEntry?.timestamp || log.deliveredAt;

                              let deliveryTimeMs = null;
                              if (resolvedPickedUpAt && resolvedDeliveredAt) {
                                deliveryTimeMs = new Date(resolvedDeliveredAt) - new Date(resolvedPickedUpAt);
                              }
                              if (!prepTimeMs && !deliveryTimeMs) return null;
                              return (
                                <div className="flex flex-wrap gap-3 mb-5 pb-4 border-b border-gray-200">
                                  {prepTimeMs != null && prepTimeMs > 0 && (
                                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
                                      <span className="text-base">⏱️</span>
                                      <div>
                                        <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide">Prep Time</p>
                                        <p className="text-sm font-bold text-blue-800">{formatDuration(prepTimeMs)}</p>
                                      </div>
                                    </div>
                                  )}
                                  {deliveryTimeMs != null && deliveryTimeMs > 0 && (
                                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5">
                                      <span className="text-base">🚚</span>
                                      <div>
                                        <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wide">Delivery Time</p>
                                        <p className="text-sm font-bold text-green-800">{formatDuration(deliveryTimeMs)}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                              {/* Left: Order Details */}
                              <div>
                                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Order Details</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    log.driverVehicle && { icon: '🚗', label: 'Vehicle', value: log.driverVehicle },
                                    log.deliveryType && { icon: '📦', label: 'Type', value: log.deliveryType },
                                    log.assignedBy && { icon: '👤', label: 'Assigned By', value: log.assignedBy },
                                    log.customerEmail && { icon: '✉️', label: 'Email', value: log.customerEmail },
                                    log.paymentMethod && { icon: '💳', label: 'Payment', value: log.paymentMethod },
                                    log.orderAmount != null && { icon: '₹', label: 'Amount', value: `₹${Number(log.orderAmount).toFixed(2)}` },
                                  ].filter(Boolean).map((item, i) => (
                                    <div key={i} className="flex items-start gap-2 bg-white rounded-lg px-3 py-2 border border-gray-100">
                                      <span className="text-sm flex-shrink-0 mt-0.5">{item.icon}</span>
                                      <div className="min-w-0">
                                        <p className="text-[10px] text-gray-400 leading-tight">{item.label}</p>
                                        <p className="text-xs font-medium text-gray-900 truncate capitalize">{item.value}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {log.deliveryDirections && (
                                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                    <p className="text-[10px] text-amber-600 font-semibold uppercase tracking-wide mb-0.5">Delivery Directions</p>
                                    <p className="text-xs text-amber-800">{log.deliveryDirections}</p>
                                  </div>
                                )}
                                {log.cancellationReason && (
                                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                    <p className="text-[10px] text-red-500 font-semibold uppercase tracking-wide mb-0.5">Cancellation Reason</p>
                                    <p className="text-xs text-red-700">{log.cancellationReason}</p>
                                  </div>
                                )}

                                {/* Broadcast Info */}
                                {(() => {
                                  const orderId = typeof log.orderId === 'object' ? log.orderId.toString() : log.orderId;
                                  const broadcast = broadcastLogsCache[orderId];
                                  if (!broadcast && loadingBroadcastLog === orderId) {
                                    return (
                                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                        <p className="text-xs text-blue-600">Loading broadcast info...</p>
                                      </div>
                                    );
                                  }
                                  if (!broadcast || !broadcast.drivers || broadcast.drivers.length === 0) return null;
                                  const accepted = broadcast.drivers.filter(d => d.status === 'accepted').length;
                                  const rejected = broadcast.drivers.filter(d => d.status === 'rejected').length;
                                  const pending = broadcast.drivers.filter(d => d.status === 'sent').length;
                                  return (
                                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-3">
                                      <p className="text-[10px] text-blue-600 font-semibold uppercase tracking-wide mb-2">Broadcast Details</p>
                                      <div className="flex gap-3 mb-2">
                                        <span className="text-[11px] font-medium text-gray-700">📡 Sent: <strong>{broadcast.drivers.length}</strong></span>
                                        <span className="text-[11px] font-medium text-green-700">✅ Accepted: <strong>{accepted}</strong></span>
                                        <span className="text-[11px] font-medium text-red-600">❌ Rejected: <strong>{rejected}</strong></span>
                                        <span className="text-[11px] font-medium text-yellow-600">⏳ Pending: <strong>{pending}</strong></span>
                                      </div>
                                      <div className="space-y-1">
                                        {broadcast.drivers.map((d, idx) => (
                                          <div key={idx} className="flex items-center justify-between bg-white rounded px-2 py-1.5 border border-gray-100">
                                            <div className="flex items-center gap-2">
                                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                                                {d.driverName?.charAt(0)?.toUpperCase() || '?'}
                                              </div>
                                              <span className="text-xs font-medium text-gray-800">{d.driverName || 'Driver'}</span>
                                            </div>
                                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                              d.status === 'accepted' ? 'bg-green-100 text-green-700' :
                                              d.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                              'bg-yellow-100 text-yellow-700'
                                            }`}>
                                              {d.status === 'sent' ? 'Pending' : d.status.charAt(0).toUpperCase() + d.status.slice(1)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Right: Status Timeline */}
                              <div>
                                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Status Timeline</p>
                                {log.statusHistory && log.statusHistory.length > 0 ? (
                                  <div className="pl-2">
                                    <div className="space-y-0">
                                      {log.statusHistory.map((sh, idx) => {
                                        const isLast = idx === log.statusHistory.length - 1;
                                        const dotColor =
                                          sh.status === 'delivered' ? 'bg-green-500 ring-green-200' :
                                          sh.status === 'cancelled' ? 'bg-red-500 ring-red-200' :
                                          sh.status === 'on_the_way' || sh.status === 'out_for_delivery' ? 'bg-orange-400 ring-orange-200' :
                                          sh.status === 'picked_up' ? 'bg-indigo-500 ring-indigo-200' :
                                          isLast ? 'bg-blue-500 ring-blue-200' : 'bg-gray-300 ring-gray-100';
                                        return (
                                          <div key={idx} className="flex items-start gap-3">
                                            <div className="flex flex-col items-center self-stretch">
                                              <div className={`w-4 h-4 rounded-full flex-shrink-0 ring-4 ${dotColor} z-10 relative flex items-center justify-center shadow-sm`}>
                                              </div>
                                              {!isLast && <div className="w-0.5 bg-gray-200 flex-1 mt-1" />}
                                            </div>
                                            <div className="flex-1 min-w-0 bg-white rounded-lg px-3 py-2 border border-gray-100 shadow-sm">
                                              <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-1.5">
                                                  <span className="text-sm">{timelineIcons[sh.status] || '📌'}</span>
                                                  <LogStatusBadge status={sh.status} />
                                                </div>
                                                <p className="text-[10px] text-gray-400 flex-shrink-0">
                                                  {sh.timestamp ? new Date(sh.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                                                </p>
                                              </div>
                                              {sh.updatedBy && (
                                                <p className="text-[10px] text-gray-400 mt-0.5">by {sh.updatedBy}</p>
                                              )}
                                              {sh.reason && (
                                                <p className="text-[10px] text-red-500 mt-0.5 font-medium">Reason: {sh.reason}</p>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="bg-white rounded-lg border border-gray-100 px-4 py-3">
                                    <p className="text-xs text-gray-500">
                                      Assigned {log.assignedAt ? new Date(log.assignedAt).toLocaleString() : 'N/A'}
                                      {log.assignedBy && ` · by ${log.assignedBy}`}
                                    </p>
                                  </div>
                                )}
                                {/* Extra milestone timestamps not in history */}
                                {log.pickedUpAt && !(log.statusHistory || []).some(s => s.status === 'picked_up') && (
                                  <div className="mt-1 flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                                    <span className="text-sm">📦</span>
                                    <p className="text-xs text-indigo-700">Picked up: {new Date(log.pickedUpAt).toLocaleString()}</p>
                                  </div>
                                )}
                                {log.deliveredAt && !(log.statusHistory || []).some(s => s.status === 'delivered') && (
                                  <div className="mt-1 flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                                    <span className="text-sm">✅</span>
                                    <p className="text-xs text-green-700">Delivered: {new Date(log.deliveredAt).toLocaleString()}</p>
                                  </div>
                                )}
                                {log.cancelledAt && !(log.statusHistory || []).some(s => s.status === 'cancelled') && (
                                  <div className="mt-1 flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                    <span className="text-sm">❌</span>
                                    <p className="text-xs text-red-700">Cancelled: {new Date(log.cancelledAt).toLocaleString()}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 bg-white rounded-lg shadow-sm p-3">
                <p className="text-xs text-gray-500">
                  Page {currentPage} of {totalPages} ({totalCount} total)
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={!hasPrevPage}
                    className="px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrevPage}
                    className="px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ← Prev
                  </button>
                  {getPageNumbers().map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1.5 text-xs font-medium rounded border ${
                        page === currentPage
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPage}
                    className="px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={!hasNextPage}
                    className="px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Drivers Tab */}
        {activeTab === "drivers" && (
          <div className="space-y-5">
            {/* Header + quick stats */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-5 sm:p-6 text-white shadow-lg">
              <div className="absolute -top-12 -right-8 h-40 w-40 rounded-full bg-indigo-500/20 blur-3xl" />
              <div className="absolute -bottom-16 -left-8 h-40 w-40 rounded-full bg-cyan-500/15 blur-3xl" />
              <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-indigo-200/80">Fleet Management</p>
                  <h2 className="text-xl sm:text-2xl font-bold mt-1">Delivery Partners</h2>
                  <p className="text-sm text-slate-300 mt-1">Manage drivers, credentials, and live availability</p>
                </div>
                <button
                  onClick={() => setShowCreateDriverModal(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-indigo-700 shadow-md hover:bg-indigo-50 transition w-full sm:w-auto"
                >
                  <span className="text-lg leading-none">+</span> Add Partner
                </button>
              </div>
              <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                {[
                  { label: "Total", value: drivers.length, tone: "from-white/15 to-white/5" },
                  { label: "Online", value: drivers.filter((d) => d.isAvailable).length, tone: "from-emerald-500/25 to-emerald-500/10" },
                  { label: "Verified", value: drivers.filter((d) => d.isVerified).length, tone: "from-sky-500/25 to-sky-500/10" },
                  { label: "Active Orders", value: drivers.reduce((s, d) => s + (Number(d.activeOrderCount) || 0), 0), tone: "from-amber-500/25 to-amber-500/10" },
                ].map((stat) => (
                  <div key={stat.label} className={`rounded-xl border border-white/10 bg-gradient-to-br ${stat.tone} px-3 py-2.5`}>
                    <p className="text-[10px] uppercase tracking-wide text-slate-300">{stat.label}</p>
                    <p className="text-xl sm:text-2xl font-extrabold mt-0.5">{stat.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Search + filters */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                <input
                  type="text"
                  placeholder="Search by name, phone, email, vehicle..."
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 focus:outline-none text-sm"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
                {[
                  { id: "all", label: "All" },
                  { id: "active", label: "Active" },
                  { id: "suspended", label: "Suspended" },
                  { id: "inactive", label: "Inactive" },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setDriverStatusFilter(f.id)}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold border transition ${
                      driverStatusFilter === f.id
                        ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                        : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Drivers list */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="rounded-2xl border border-gray-200 bg-white p-5 animate-pulse">
                    <div className="flex gap-3">
                      <div className="h-12 w-12 rounded-2xl bg-gray-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                        <div className="h-3 bg-gray-100 rounded w-1/2" />
                      </div>
                    </div>
                    <div className="mt-4 h-16 bg-gray-100 rounded-xl" />
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      <div className="h-8 bg-gray-100 rounded-lg" />
                      <div className="h-8 bg-gray-100 rounded-lg" />
                      <div className="h-8 bg-gray-100 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            ) : drivers.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-white py-14 px-6 text-center">
                <div className="mx-auto h-16 w-16 rounded-2xl bg-indigo-50 flex items-center justify-center text-3xl mb-4">🚗</div>
                <h3 className="text-lg font-bold text-gray-900">No delivery partners yet</h3>
                <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                  Add your first driver to start assigning deliveries and tracking fleet performance.
                </p>
                <button
                  onClick={() => setShowCreateDriverModal(true)}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition shadow-md"
                >
                  + Create First Partner
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {drivers.map((driver) => (
                  <DriverPartnerCard
                    key={driver._id}
                    driver={driver}
                    onVerify={() => handleVerifyDriver(driver._id, true)}
                    onEdit={() => openEditDriver(driver)}
                    onToggleStatus={() => handleToggleDriverStatus(driver._id, driver.status)}
                    onSync={() => handleSyncOrderCount(driver._id)}
                    onDelete={() => handleDeleteDriver(driver)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Delivery Settings</h2>
            <div className="space-y-6">
              {/* Auto-assign toggle */}
              <SettingToggle
                label="Auto-Assign Drivers"
                description="Automatically assign the nearest available driver when an order is confirmed"
                checked={settings.autoAssignDriver}
                onChange={(val) => setSettings((prev) => ({ ...prev, autoAssignDriver: val }))}
              />

              {/* OTP toggle */}
              <SettingToggle
                label="OTP Verification Required"
                description="Require delivery OTP verification from customer before marking order as delivered"
                checked={settings.otpVerificationRequired}
                onChange={(val) => setSettings((prev) => ({ ...prev, otpVerificationRequired: val }))}
              />

              {/* Customer notifications */}
              <SettingToggle
                label="Notify Customer on Driver Assignment"
                description="Send push notification to customer when a driver is assigned"
                checked={settings.notifyCustomerOnAssignment}
                onChange={(val) => setSettings((prev) => ({ ...prev, notifyCustomerOnAssignment: val }))}
              />

              <SettingToggle
                label="Auto-Broadcast Orders to Drivers"
                description="Automatically broadcast confirmed orders to all active drivers. First driver to accept gets assigned."
                checked={settings.autoBroadcastToDrivers}
                onChange={(val) => setSettings((prev) => ({ ...prev, autoBroadcastToDrivers: val }))}
              />

              <SettingToggle
                label="Allow Driver Cancellation"
                description="Allow drivers to cancel assigned orders"
                checked={settings.allowDriverCancellation}
                onChange={(val) => setSettings((prev) => ({ ...prev, allowDriverCancellation: val }))}
              />

              {/* Numeric settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Orders Per Driver</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={settings.maxOrdersPerDriver}
                    onChange={(e) => setSettings((prev) => ({ ...prev, maxOrdersPerDriver: parseInt(e.target.value) || 5 }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Radius (km)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={settings.assignmentRadius}
                    onChange={(e) => setSettings((prev) => ({ ...prev, assignmentRadius: parseInt(e.target.value) || 10 }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Delivery Hours Section */}
              <div className="border-t border-gray-200 pt-6 mt-2">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Delivery Operating Hours</h3>
                <p className="text-xs text-gray-500 mb-4">Set the opening and closing hours for delivery operations. Expected delivery times shown to customers will be calculated based on these hours.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Opening Time</label>
                    <input
                      type="time"
                      value={settings.deliveryOpeningTime || '08:00'}
                      onChange={(e) => setSettings((prev) => ({ ...prev, deliveryOpeningTime: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">Orders placed before this time will start delivery calculation from this time</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Closing Time</label>
                    <input
                      type="time"
                      value={settings.deliveryClosingTime || '22:00'}
                      onChange={(e) => setSettings((prev) => ({ ...prev, deliveryClosingTime: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">Delivery operations stop at this time; remaining time rolls to next day</p>
                  </div>
                </div>
              </div>

              {/* Map Visibility Section */}
              <div className="border-t border-gray-200 pt-6 mt-2">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Live Tracking Map Visibility</h3>
                <p className="text-xs text-gray-500 mb-4">Control when the live delivery tracking map is shown to customers on the order details page.</p>
                
                <div className="space-y-3">
                  {/* Option: Hidden */}
                  <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderColor: settings.mapVisibility?.mode === 'hidden' ? '#3B82F6' : '#E5E7EB', backgroundColor: settings.mapVisibility?.mode === 'hidden' ? '#EFF6FF' : 'transparent' }}
                  >
                    <input
                      type="radio"
                      name="mapVisibility"
                      value="hidden"
                      checked={settings.mapVisibility?.mode === 'hidden'}
                      onChange={() => setSettings((prev) => ({ ...prev, mapVisibility: { ...prev.mapVisibility, mode: 'hidden' } }))}
                      className="mt-0.5 accent-blue-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Hide Map Completely</p>
                      <p className="text-xs text-gray-500">The live tracking map will not be visible to customers at all.</p>
                    </div>
                  </label>

                  {/* Option: Show from status */}
                  <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderColor: settings.mapVisibility?.mode === 'show_from_status' ? '#3B82F6' : '#E5E7EB', backgroundColor: settings.mapVisibility?.mode === 'show_from_status' ? '#EFF6FF' : 'transparent' }}
                  >
                    <input
                      type="radio"
                      name="mapVisibility"
                      value="show_from_status"
                      checked={settings.mapVisibility?.mode !== 'hidden'}
                      onChange={() => setSettings((prev) => ({ ...prev, mapVisibility: { ...prev.mapVisibility, mode: 'show_from_status' } }))}
                      className="mt-0.5 accent-blue-600"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Show Map from a Specific Status</p>
                      <p className="text-xs text-gray-500 mb-2">The map will appear when the order reaches the selected status.</p>
                      
                      {settings.mapVisibility?.mode !== 'hidden' && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {['Confirmed', 'Shipped', 'Out For Delivery'].map((status) => (
                            <button
                              key={status}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setSettings((prev) => ({
                                  ...prev,
                                  mapVisibility: { mode: 'show_from_status', showFromStatus: status },
                                }));
                              }}
                              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                                settings.mapVisibility?.showFromStatus === status
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                              }`}
                            >
                              {status}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {/* Status flow hint */}
                <div className="mt-3 flex items-center gap-1.5 text-[11px] text-gray-400">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Status flow: Received → Confirmed → Shipped → Out For Delivery → Delivered</span>
                </div>
              </div>

              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        )}

        {/* Create Driver Modal */}
        {showCreateDriverModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 m-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Delivery Partner</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={driverForm.name}
                    onChange={(e) => setDriverForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Enter driver name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={driverForm.email}
                    onChange={(e) => setDriverForm((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="driver@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={driverForm.phone}
                    onChange={(e) => setDriverForm((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="10-digit phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Driving License Number *</label>
                  <input
                    type="text"
                    value={driverForm.drivingLicenseNumber}
                    onChange={(e) => setDriverForm((prev) => ({ ...prev, drivingLicenseNumber: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="DL number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">License Expiry Date</label>
                  <input
                    type="date"
                    value={driverForm.drivingLicenseExpiry}
                    onChange={(e) => setDriverForm((prev) => ({ ...prev, drivingLicenseExpiry: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                    <select
                      value={driverForm.vehicleType}
                      onChange={(e) => setDriverForm((prev) => ({ ...prev, vehicleType: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="bike">Bike</option>
                      <option value="scooter">Scooter</option>
                      <option value="car">Car</option>
                      <option value="van">Van</option>
                      <option value="truck">Truck</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
                    <input
                      type="text"
                      value={driverForm.vehicleNumber}
                      onChange={(e) => setDriverForm((prev) => ({ ...prev, vehicleNumber: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="KA01AB1234"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    ref={addressInputRef}
                    type="text"
                    value={driverForm.address}
                    onChange={(e) => setDriverForm((prev) => ({ ...prev, address: e.target.value, addressCoordinates: null }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Search address..."
                  />
                  {driverForm.addressCoordinates && (
                    <p className="text-xs text-green-600 mt-1">
                      📍 Location captured ({driverForm.addressCoordinates.lat.toFixed(4)}, {driverForm.addressCoordinates.lng.toFixed(4)})
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact</label>
                  <input
                    type="tel"
                    value={driverForm.emergencyContact}
                    onChange={(e) => setDriverForm((prev) => ({ ...prev, emergencyContact: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Emergency contact number"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreateDriverModal(false)}
                  className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDriver}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                >
                  {loading ? "Creating..." : "Create Driver"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Driver Modal */}
        {showEditDriverModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 m-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Edit Delivery Partner</h2>
              <p className="text-xs text-gray-500 mb-4">Update partner details. Email cannot be changed (used for login).</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={editDriverForm.name}
                    onChange={(e) => setEditDriverForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="Enter driver name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email (login)</label>
                  <input
                    type="email"
                    value={editDriverForm.email}
                    readOnly
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={editDriverForm.phone}
                    onChange={(e) => setEditDriverForm((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="10-digit phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Driving License Number *</label>
                  <input
                    type="text"
                    value={editDriverForm.drivingLicenseNumber}
                    onChange={(e) => setEditDriverForm((prev) => ({ ...prev, drivingLicenseNumber: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="DL number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">License Expiry Date</label>
                  <input
                    type="date"
                    value={editDriverForm.drivingLicenseExpiry}
                    onChange={(e) => setEditDriverForm((prev) => ({ ...prev, drivingLicenseExpiry: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                    <select
                      value={editDriverForm.vehicleType}
                      onChange={(e) => setEditDriverForm((prev) => ({ ...prev, vehicleType: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    >
                      <option value="bike">Bike</option>
                      <option value="scooter">Scooter</option>
                      <option value="car">Car</option>
                      <option value="van">Van</option>
                      <option value="truck">Truck</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
                    <input
                      type="text"
                      value={editDriverForm.vehicleNumber}
                      onChange={(e) => setEditDriverForm((prev) => ({ ...prev, vehicleNumber: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholder="KA01AB1234"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    ref={editAddressInputRef}
                    type="text"
                    value={editDriverForm.address}
                    onChange={(e) => setEditDriverForm((prev) => ({ ...prev, address: e.target.value, addressCoordinates: null }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="Search address..."
                  />
                  {editDriverForm.addressCoordinates && (
                    <p className="text-xs text-green-600 mt-1">
                      📍 Location captured ({editDriverForm.addressCoordinates.lat.toFixed(4)}, {editDriverForm.addressCoordinates.lng.toFixed(4)})
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact</label>
                  <input
                    type="tel"
                    value={editDriverForm.emergencyContact}
                    onChange={(e) => setEditDriverForm((prev) => ({ ...prev, emergencyContact: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="Emergency contact number"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={closeEditDriver}
                  className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateDriver}
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Summary card component (replaces dashboard StatCard, matches inventory-logs gradient style)
const SummaryCard = ({ title, value, subtitle, gradient }) => (
  <div className={`bg-gradient-to-br ${gradient} rounded-lg p-4 text-white shadow-sm`}>
    <p className="text-xs font-medium opacity-80">{title}</p>
    <p className="text-2xl font-bold mt-1">{value}</p>
    {subtitle && <p className="text-[10px] mt-1 opacity-70">{subtitle}</p>}
  </div>
);

const VEHICLE_EMOJI = { bike: "🏍️", scooter: "🛵", car: "🚗", van: "🚐", truck: "🚛" };

const copyText = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied!`);
};

const DriverPartnerCard = ({ driver, onVerify, onEdit, onToggleStatus, onSync, onDelete }) => {
  const vehicleEmoji = VEHICLE_EMOJI[driver.vehicleType] || "🚗";
  const isActive = driver.status === "active";
  const activeOrders = Number(driver.activeOrderCount) || 0;

  return (
    <article className="group rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-indigo-200/80 transition-all duration-200 overflow-hidden flex flex-col">
      {/* Card header */}
      <div className="p-4 sm:p-5 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-start gap-3">
          <div className="relative flex-shrink-0">
            <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-2xl flex items-center justify-center text-lg sm:text-xl font-bold text-white shadow-md ${
              driver.isAvailable
                ? "bg-gradient-to-br from-emerald-500 to-teal-600"
                : "bg-gradient-to-br from-slate-400 to-slate-500"
            }`}>
              {driver.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <span className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white ${
              driver.isAvailable ? "bg-emerald-500 animate-pulse" : "bg-gray-400"
            }`} title={driver.isAvailable ? "Online" : "Offline"} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{driver.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{driver.isAvailable ? "🟢 Online now" : "⚫ Offline"}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full ${
                isActive ? "bg-emerald-100 text-emerald-700" :
                driver.status === "suspended" ? "bg-red-100 text-red-700" :
                "bg-gray-100 text-gray-600"
              }`}>
                {driver.status}
              </span>
              <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full ${
                driver.isVerified ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"
              }`}>
                {driver.isVerified ? "✓ Verified" : "Pending verify"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="p-4 sm:p-5 space-y-3 flex-1">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-gray-50 px-3 py-2.5 border border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase">Deliveries</p>
            <p className="text-lg font-bold text-gray-900">{driver.totalDeliveries || 0}</p>
          </div>
          <div className={`rounded-xl px-3 py-2.5 border ${
            activeOrders > 0 ? "bg-amber-50 border-amber-100" : "bg-gray-50 border-gray-100"
          }`}>
            <p className="text-[10px] font-semibold text-gray-400 uppercase">Active now</p>
            <p className={`text-lg font-bold ${activeOrders > 0 ? "text-amber-600" : "text-gray-900"}`}>{activeOrders}</p>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base flex-shrink-0">📞</span>
            <a href={`tel:${driver.phone}`} className="text-gray-800 font-medium hover:text-indigo-600 truncate">{driver.phone}</a>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base flex-shrink-0">📧</span>
            <span className="text-gray-600 truncate text-xs sm:text-sm" title={driver.email}>{driver.email}</span>
            <button
              type="button"
              onClick={() => copyText(driver.email, "Email")}
              className="ml-auto flex-shrink-0 text-[10px] px-2 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 font-semibold"
            >
              Copy
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-base">{vehicleEmoji}</span>
            <span className="text-gray-800 capitalize font-medium">{driver.vehicleType || "—"}</span>
            {driver.vehicleNumber && (
              <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded-md">{driver.vehicleNumber}</span>
            )}
          </div>
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <span className="text-base flex-shrink-0">🪪</span>
            <span className="break-all">DL: {driver.drivingLicenseNumber || "—"}</span>
          </div>
        </div>

        {/* Login credentials */}
        <div className="rounded-xl border border-dashed border-gray-200 bg-slate-50/80 px-3 py-2.5">
          <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5">App Login</p>
          {driver.isTempPassword && driver.tempPassword ? (
            <div className="flex items-center gap-2">
              <code className="text-xs font-mono text-orange-600 bg-orange-50 px-2 py-1 rounded-lg flex-1 truncate">
                {driver.tempPassword}
              </code>
              <button
                type="button"
                onClick={() => copyText(driver.tempPassword, "Temp password")}
                className="text-[10px] px-2 py-1 rounded-lg bg-orange-100 text-orange-700 font-semibold hover:bg-orange-200 flex-shrink-0"
              >
                Copy
              </button>
            </div>
          ) : (
            <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1">✓ Password configured</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="p-3 sm:p-4 bg-gray-50/80 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {!driver.isVerified && (
          <button
            type="button"
            onClick={onVerify}
            className="col-span-2 sm:col-span-3 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-sky-600 text-white text-xs font-bold hover:bg-sky-700 transition"
          >
            ✓ Verify Partner
          </button>
        )}
        <button
          type="button"
          onClick={onEdit}
          className="py-2 rounded-xl bg-white border border-gray-200 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 hover:border-indigo-200 transition"
        >
          ✏️ Edit
        </button>
        <button
          type="button"
          onClick={onToggleStatus}
          className={`py-2 rounded-xl border text-xs font-semibold transition ${
            isActive
              ? "bg-white border-red-200 text-red-600 hover:bg-red-50"
              : "bg-white border-emerald-200 text-emerald-600 hover:bg-emerald-50"
          }`}
        >
          {isActive ? "⏸ Suspend" : "▶ Activate"}
        </button>
        <button
          type="button"
          onClick={onSync}
          title="Recalculate active order count"
          className="py-2 rounded-xl bg-white border border-gray-200 text-xs font-semibold text-amber-700 hover:bg-amber-50 transition"
        >
          🔄 Sync
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="col-span-2 sm:col-span-3 py-2 rounded-xl bg-white border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 transition"
        >
          🗑 Delete Partner
        </button>
      </div>
    </article>
  );
};

// Reusable toggle component
const SettingToggle = ({ label, description, checked, onChange }) => {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
      </label>
    </div>
  );
};

// Log status badge component
const LogStatusBadge = ({ status, small }) => {
  const config = {
    assigned:         { bg: 'bg-blue-50',   text: 'text-blue-700',   dot: 'bg-blue-500',   label: 'Assigned' },
    picked_up:        { bg: 'bg-indigo-50', text: 'text-indigo-700', dot: 'bg-indigo-500', label: 'Picked Up' },
    on_the_way:       { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400', label: 'Out for Delivery' },
    out_for_delivery: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400', label: 'Out for Delivery' },
    delivered:        { bg: 'bg-green-50',  text: 'text-green-700',  dot: 'bg-green-500',  label: 'Delivered' },
    cancelled:        { bg: 'bg-red-50',    text: 'text-red-700',    dot: 'bg-red-500',    label: 'Cancelled' },
  };
  const c = config[status] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: (status || '').replace(/_/g, ' ') };
  return (
    <span className={`inline-flex items-center gap-1 ${c.bg} ${c.text} font-medium rounded-full capitalize border border-opacity-20 ${
      small ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2.5 py-1'
    }`} style={{ borderColor: 'currentColor', borderOpacity: 0.15 }}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} flex-shrink-0 ${small ? 'hidden' : ''}`} />
      {c.label}
    </span>
  );
};

export default DeliveryControls;
